import { formatRuleName } from './formatters';

// Rules manager to handle rule storage and application
const RULES_STORAGE_KEY = 'rules';
const RULES_ERROR_DUPLICATE = 'A rule with identical filters and actions already exists';

class RulesManager {
  constructor() {
    this.loadRules();
  }

  loadRules() {
    // First check for saved database data
    const savedDatabaseRules = localStorage.getItem('savedRules');
    if (savedDatabaseRules) {
      this.rules = JSON.parse(savedDatabaseRules);
      // Save to regular storage and clean up
      this.saveRules();
      localStorage.removeItem('savedRules');
    } else {
      // Fall back to regular saved rules
      const savedRules = localStorage.getItem(RULES_STORAGE_KEY);
      this.rules = savedRules ? JSON.parse(savedRules) : [];
    }
    
    // Convert date strings back to Date objects in filters
    this.rules.forEach(rule => {
      if (rule.filters.filterStartDate) {
        rule.filters.filterStartDate = new Date(rule.filters.filterStartDate);
      }
      if (rule.filters.filterEndDate) {
        rule.filters.filterEndDate = new Date(rule.filters.filterEndDate);
      }
      if (rule.filters.minDate) {
        rule.filters.minDate = new Date(rule.filters.minDate);
      }
      if (rule.filters.maxDate) {
        rule.filters.maxDate = new Date(rule.filters.maxDate);
      }
    });
  }

  saveRules() {
    localStorage.setItem(RULES_STORAGE_KEY, JSON.stringify(this.rules));
  }

  getRules() {
    return this.rules.map(rule => ({
      ...rule,
      name: formatRuleName(rule.filters, rule.actions)
    }));
  }
  
  setRules(rules) {
    if (Array.isArray(rules)) {
      this.rules = rules;
      this.saveRules();
    }
    return this.getRules();
  }

  isDuplicateRule(newRule, excludeRuleId = null) {
    return this.rules.some(existingRule => {
      // Skip comparing with the rule being edited
      if (excludeRuleId && existingRule.id === excludeRuleId) {
        return false;
      }
      
      // Check if filters match
      const filtersMatch = this.areFiltersEqual(existingRule.filters, newRule.filters);
      
      // Check if actions match
      const actionsMatch = this.areActionsEqual(existingRule.actions, newRule.actions);
      
      return filtersMatch && actionsMatch;
    });
  }

  areFiltersEqual(filters1, filters2) {
    return JSON.stringify(filters1) === JSON.stringify(filters2);
  }

  areActionsEqual(actions1, actions2) {
    if (actions1.length !== actions2.length) return false;
    
    // Sort actions to ensure consistent comparison
    const sortedActions1 = [...actions1].sort((a, b) => 
      `${a.action}${a.value}`.localeCompare(`${b.action}${b.value}`));
    const sortedActions2 = [...actions2].sort((a, b) => 
      `${a.action}${a.value}`.localeCompare(`${b.action}${b.value}`));
    
    return sortedActions1.every((action, index) => 
      action.action === sortedActions2[index].action && 
      action.value === sortedActions2[index].value
    );
  }

  addRule(rule) {
    // Check for duplicates before adding
    if (this.isDuplicateRule(rule)) {
      throw new Error(RULES_ERROR_DUPLICATE);
    }

    const newRule = {
      ...rule,
      id: Date.now().toString(),
      order: this.rules.length + 1
    };
    this.rules.push(newRule);
    this.saveRules();
    return this.getRules();
  }

  updateRule(ruleId, updatedRule) {
    // Check for duplicates before updating, excluding the current rule
    if (this.isDuplicateRule(updatedRule, ruleId)) {
      throw new Error(RULES_ERROR_DUPLICATE);
    }

    const index = this.rules.findIndex(rule => rule.id === ruleId);
    if (index === -1) {
      throw new Error('Rule not found');
    }

    // Update the rule while preserving its id and order
    this.rules[index] = {
      ...updatedRule,
      id: ruleId,
      order: this.rules[index].order
    };
    
    this.saveRules();
    return this.getRules();
  }

  updateRuleOrder(rules) {
    this.rules = rules.map((rule, index) => ({
      ...rule,
      order: index + 1
    }));
    this.saveRules();
    return this.getRules();
  }

  deleteRule(ruleId) {
    this.rules = this.rules.filter(rule => rule.id !== ruleId);
    this.rules = this.rules.map((rule, index) => ({
      ...rule,
      order: index + 1
    }));
    this.saveRules();
    return this.getRules();
  }
}

export const rulesManager = new RulesManager();
